import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { injectMutation, injectQuery, injectQueryClient } from '@ngneat/query'
import { queryKeys } from './query-keys'
import { ToastService } from './toast.service'
import { ClientSettings } from './client-settings'

export type ClientOrganization = {
  id: number
  name: string
  deactivated: boolean
}

export type Client = {
  id: number
  name: string
  isInternal: boolean
  createdAt: Date
  organizations: ClientOrganization[]
} & ClientSettings

export type ClientToBeCreated = {
  name: string
  isInternal: boolean
  organizationIds: number[]
} & Partial<ClientSettings>

export type ClientUpdateData = {
  id: number
  name: string
  isInternal: boolean
  organizationIds: number[]
} & Partial<ClientSettings>

@Injectable({
  providedIn: 'root',
})
export class ClientsService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  mutation = injectMutation()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)

  getClients() {
    return this.query({
      queryKey: queryKeys.clients.all,
      queryFn: () => this.httpClient.get<Client[]>('/api/clients'),
    }).result$
  }

  getClient(id: number) {
    return this.query({
      queryKey: queryKeys.clients.byId(id),
      queryFn: () => this.httpClient.get<Client>(`/api/clients/${id}`),
    }).result$
  }

  getCurrentUserClient() {
    return this.query({
      queryKey: queryKeys.clients.me,
      queryFn: () => this.httpClient.get<Client>('/api/clients/me'),
    }).result$
  }

  createClient() {
    return this.mutation({
      mutationFn: (client: ClientToBeCreated) => this.httpClient.post<Client>('/api/clients', client),
      onSuccess: () => this.invalidate(),
      onError: () => {
        this.toastService.show('CLIENTS.ADMIN.CREATE_FAILED')
      },
    })
  }

  updateClient() {
    return this.mutation({
      mutationFn: ({ id, ...client }: ClientUpdateData) => this.httpClient.patch<Client>(`/api/clients/${id}`, client),
      onSuccess: () => this.invalidate(),
      onError: () => {
        this.toastService.show('CLIENTS.ADMIN.EDIT_FAILED')
      },
    })
  }

  deleteClient() {
    return this.mutation({
      mutationFn: (id: number) => this.httpClient.delete<void>(`/api/clients/${id}`),
      onSuccess: () => this.invalidate(),
      onError: () => {
        this.toastService.show('CLIENTS.ADMIN.DELETE_FAILED')
      },
    })
  }

  // Reassigning organizations changes what the organization lists and badges show, so those are
  // refreshed alongside the clients themselves.
  private invalidate() {
    this.queryClient.invalidateQueries({ queryKey: queryKeys.clients.base })
    this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.base })
    this.queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all })
  }
}
